import colorPallete, { buttonFonts } from "@DSG/MobiusOverrides/styles/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPagesByParent } from "@insite/client-framework/Services/ContentService";
import { PageModel } from "@insite/client-framework/Types/PageProps";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconProps } from "@insite/mobius/Icon";
import { MappedLink } from "@DSG/MobiusOverrides/Menu/Menu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface PanelMenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<PanelMenuPresentationProps>;
        children?: StyledProp<PanelMenuPresentationProps>;
        menu?: StyledProp<PanelMenuPresentationProps>;
    };
    /** Props passed to the typography children of the menu.
     * @themable */
    childTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu close icon.
     * @themable */
    closeIconProps?: IconProps;
    /** Props passed to the menu back icon.
     * @themable */
    backIconProps?: IconProps;
    /** Color of the background of the menu header.
     * @themable */
    headerColor?: string;
    /** Color of the background of the menu body.
     * @themable */
    bodyColor?: string;
    /** Props to be passed into the panel row component.
     * @themable */
    panelRowProps?: PanelRowPresentationProps;
}

export type PanelMenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** The element that triggers the menu. */
        panelTrigger: React.ReactElement;
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /**
         * The nested child structure that will be rendered by the menu.
         * If menu items are provided, children organically passed to the component
         * will not be rendered.
         */
        menuItems?: MappedLink[];
        /** Maximum number of menus that will appear in submenu panels. */
        maxDepth?: number;
        /** Function called to close overlay (Drawer) in which menu resides. Governs presence of `x` icons in headers. */
        closeOverlay?: () => void;
        currentParentTitle?: string;
        parentUrl?: string;
        parentCategoryName?: string;
        parentCategoryUrl?: string;
        /** The current URL of the application, used to apply 'current page' styles to the menu item */
        currentUrl?: string;
        /** @ignore internal prop */
        layer: number;
        currentLanguageId?:any;
        defaultLanguageId?:any;
        parentNodeId?:string;
        menuLinks?:MappedLink[]|undefined;
    }
>;

const PanelMenuWrapper = styled.nav<ThemeProps<BaseTheme> & InjectableCss>`
    button,
    link {
        &:focus {
            outline-offset: -${getProp("theme.focus.width")};
        }
    }
    > button {
        width: 100%;
    }
    &:focus > *:first-child {
        outline-color: ${getProp("theme.focus.color", "#09f")};
        outline-style: ${getProp("theme.focus.style", "solid")};
        outline-width: ${getProp("theme.focus.width", "2px")};
        outline-offset: -${getProp("theme.focus.width")};
    }
    outline: none;
    ${injectCss}
`;

const PanelMenuStyle = styled.div<ThemeProps<BaseTheme> & InjectableCss & { color?: string }>`
    height: calc(100% - 50px);
    overflow-y: auto;
    background: ${({ color, theme }) => resolveColor(color, theme)};

    > .mobile-parent-category-link {
        &.industries {
            a {
                display: none;
            }
        }
    }
    
    ${injectCss}
`;

const PanelChildren = styled.div<ThemeProps<BaseTheme> & InjectableCss & { displayLayer: number }>`
    max-height: 100vh;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 100%;
    transition: all ease-in-out ${getProp("theme.transition.duration.short")}ms;
    ${({ displayLayer }) => {
        if (displayLayer <= 0) {
            return css`
                left: 0;
            `;
        }
        return css`
            left: 100%;
        `;
    }}
    .close-button {
        background: ${colorPallete.white};
        [class*="IconWrapper"] {
            color: ${colorPallete.grey800};
        }
        @media only screen and (max-width: 768px) {
            padding: 0;
        }
    }
    .back-button {
        display: flex;
        align-items: center;

        @media only screen and (max-width: 768px) {
            padding: 0;
        }

        span {
            color: ${colorPallete.primaryRed500};
            font-size: ${buttonFonts.buttonSm.fontSize};
            line-height: ${buttonFonts.buttonSm.lineHeight};
            font-weight: ${buttonFonts.buttonSm.fontWeight};
            padding-top: 1px;

            &:nth-child(2) {
                padding-top: 2px;
            }
        }
    }
    ${injectCss}
`;

/** PanelMenuProps with defaulted properties now listed as not-optional, plus theme. */
export type PanelMenuProps = PanelMenuComponentProps & PanelMenuPresentationProps;

type PanelMenuPropsCompleted = PanelMenuPresentationProps &
    Omit<PanelMenuComponentProps, "maxDepth"> &
    Required<Pick<PanelMenuComponentProps, "maxDepth">>;

class PanelMenu extends React.Component<PanelMenuPropsCompleted> {
    static defaultProps = { maxDepth: 3 };

    state = {
        layer: this.props.layer + 1,
        descriptionId: this.props.descriptionId || uniqueId(),
        menuLinks:this.props.menuItems,
        
    };

    UNSAFE_componentWillReceiveProps(nextProps: PanelMenuPropsCompleted) {
        // eslint-disable-line camelcase
        if (nextProps.layer !== this.props.layer + 1 && !nextProps.children && !this.props.children) {
            this.setState({ layer: nextProps.layer + 1 }, () => {});
        }
    }

    moveNextLeft = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer - 1 };
        });
    };

    moveNextRight = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer + 1 };
        });
    };
    componentDidMount() {
        if(this.props.menuItems)
        {
            let customLinks:MappedLink[] | undefined;
            customLinks = { ...this.props.menuItems };
            const languageKey = this.props.currentLanguageId ?? this.props.defaultLanguageId;
            customLinks =  this.updateCustomPageTitle(this.props.menuItems,languageKey);
            
            this.setState({ menuLinks: customLinks });
        }
        
    }
    
    updateCustomPageTitle = (staticPagelink:MappedLink[],languageId:any) => {
        let childOverrideTitle : MappedLink[]=[];
        if(staticPagelink)
        {
            
            if(this.props.parentNodeId)
            {
                const tempParameter = { parentNodeId: this.props.parentNodeId };
                var dsgCustomPages: PageModel[];
                 getPagesByParent(tempParameter).then(result =>{
                    dsgCustomPages = result?.pages;
                    if(dsgCustomPages)
                    {
                        staticPagelink.forEach((child) => {
                            if(child.type == "DsgCustomPage" && child.url)
                            { 
                                let childLink:MappedLink | undefined;
                                childLink = { ...child };
                                let page = undefined;
                                    if(dsgCustomPages && dsgCustomPages.filter(x=>x.nodeId === child.id))
                                    {
                                        page = dsgCustomPages.filter(x=>x.nodeId === child.id)[0];
                                    }
                                  
                                    if(page && page.translatableFields && page?.translatableFields["overrideTitle"] && page?.translatableFields["overrideTitle"][languageId] && childLink)
                                    {
                                        childLink.title = page?.translatableFields["overrideTitle"][languageId];
                                        childOverrideTitle.push(childLink);
                                    }
                                    else
                                    {
                                        childOverrideTitle.push(child);
                                    }
                            }
                            else
                            {
                                childOverrideTitle.push(child);
                            }
                        });
                        return childOverrideTitle;
                    }
                });
            }
            else
            {
                childOverrideTitle = staticPagelink;
            }
           
        }
        return childOverrideTitle;
}

    renderMenuItems(childMenuItems: MappedLink[], currentDepth: number, propBuilder: any, parentTitle: string, parentUrl: string) {
        const { applyProp } = propBuilder;
        const { currentUrl } = this.props;
        const typographyProps = applyProp("childTypographyProps");
        const panelRowProps = applyProp("panelRowProps");

        const currentUrlRegex = new RegExp(currentUrl!, "gi");
        return (
            <>  
                <StyledWrapper className={`mobile-parent-category-link ${this.props.parentCategoryName?.toLowerCase() === "industries" ? this.props.parentCategoryName.toLowerCase() : ''}`}>
                    <p className="parent-title-mobile">{parentTitle && parentTitle !== "" ? parentTitle : this.props.parentCategoryName}</p>
                    {
                        (parentUrl || this.props.parentCategoryUrl) && (
                            <a href={parentUrl && parentUrl !== "" ? parentUrl : this.props.parentCategoryUrl} className="text-link text-link-primary">See all</a>
                        )
                    }
                </StyledWrapper>
               
                {childMenuItems
                    .filter(item => !item.excludeFromNavigation)
                    .map(item => {
                        const renderChildren =
                            item.children?.filter(child => !child.excludeFromNavigation).length &&
                            currentDepth < this.props.maxDepth;
                        const clickableProps: { href?: string } = { ...item.clickableProps };
                        if (!renderChildren && item.url) {
                            clickableProps.href = item.url;
                        }
                        const menuItem = (
                            <PanelRow
                                key={item.title}
                                isCurrent={item.url?.match(currentUrlRegex) || item.title?.match(currentUrlRegex)}
                                onClick={this.props.closeOverlay}
                                color="white"
                                hasChildren={renderChildren || false}
                                tabIndex={this.state.layer === 0 ? 0 : -1}
                                css={css`
                                    width: 100%;
                                `}
                                {...clickableProps}
                                {...panelRowProps}
                                className={item.url?.match(currentUrlRegex) || item.title?.match(currentUrlRegex) ? 'active' : 'inActive'} 
                            >
                                <Typography
                                    className="sample"
                                    data-test-selector={`${this.props["data-test-selector"]}_${item.title}`}
                                    size={15}
                                    {...typographyProps}
                                >
                                    {item.title}
                                </Typography>
                            </PanelRow>
                        );
                        if (renderChildren) {
                            return (
                                <PanelMenu
                                    {...this.props}
                                    as="div"
                                    key={item.title}
                                    currentParentTitle={item.title}
                                    parentUrl={item.url}
                                    panelTrigger={menuItem}
                                    menuItems={item.children as MappedLink[]}
                                    maxDepth={this.props.maxDepth - 1}
                                    layer={this.state.layer}
                                />
                            );
                        }
                        return menuItem;
                    })}
            </>
        );
    }

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const {
                        currentUrl,
                        descriptionId,
                        menuItems,
                        panelTrigger,
                        closeOverlay,
                        mergeCss,
                        currentParentTitle,
                        parentUrl,
                        ...otherProps
                    } = this.props;
                    const propBuilder = applyPropBuilder({ ...this.props, theme }, { component: "panelMenu" });
                    const { translate } = theme;
                    const { layer, descriptionId: navId } = this.state;
                    const { spreadProps, applyProp, applyStyledProp } = propBuilder;
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.panelMenu?.defaultProps?.mergeCss;
                    const headerColor = applyProp("headerColor", "common.backgroundContrast");
                    return (
                        <PanelMenuWrapper
                            css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                            {...{ "aria-labelledby": navId }}
                            {...otherProps}
                            tabIndex={-1}
                        >
                            {React.cloneElement(panelTrigger, { id: navId, onClick: this.moveNextLeft })}
                            <PanelChildren
                                displayLayer={layer}
                                aria-hidden={layer > 0}
                                css={applyStyledProp(["cssOverrides", "children"], resolvedMergeCss)}
                                data-layer={layer}
                                color={colorPallete.white}
                            >
                                <PanelRow as="div" header color={colorPallete.white} className="mobile-menu-header">
                                    <Button
                                        color="none"
                                        className="back-button"
                                        onClick={this.moveNextRight}
                                        tabIndex={layer === 0 ? 0 : -1}
                                    >
                                        <ButtonIcon {...spreadProps("backIconProps")} color={colorPallete.primaryRed500} size={20} />
                                        <span>Back</span>
                                    </Button>
                                    {closeOverlay && (
                                        <Button
                                            onClick={closeOverlay}
                                            tabIndex={layer === 0 ? 0 : -1}
                                            className="close-button"
                                        >
                                            <ButtonIcon {...spreadProps("closeIconProps")} />
                                            <VisuallyHidden>{translate("Close")}</VisuallyHidden>
                                        </Button>
                                    )}
                                </PanelRow>
                                <PanelMenuStyle
                                    css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                    color={colorPallete.white}
                                >
                                    {this.state.menuLinks ? (
                                        this.renderMenuItems(this.state.menuLinks!, 1, propBuilder, currentParentTitle as string, parentUrl as string)
                                    ) : (
                                        <>{this.props.children}</>
                                    )}
                                </PanelMenuStyle>
                            </PanelChildren>
                        </PanelMenuWrapper>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/**
 * Menu composed of slide-in panels.
 */

PanelMenu.defaultProps = {
    maxDepth: 3,
};

/** @component */
export default withTheme((props: PanelMenuProps) => <PanelMenu {...(props as PanelMenuPropsCompleted)} />);
